import React, { useMemo } from 'react';

import classNames from 'classnames';

import type { PeriodJobAssignmentDetails } from 'state/__generated__/tadaahAPI';
import Tooltip from 'components/tooltip/tooltip';
import Typography from 'components-new/typography/typography';

import { createLocale } from 'locales';

const localize = createLocale('organisationPeriodJobsLocales');

interface ProgressBarTypes {
  assignmentDetails: PeriodJobAssignmentDetails;
  className?: string;
}

interface ProgressBarElement {
  id: number | string;
  percentage: number;
  className: string;
  status: string;
}

const ProgressBar: React.FC<ProgressBarTypes> = (props) => {
  const { assignmentDetails, className } = props;
  const details = useMemo(() => {
    if (assignmentDetails.assignments.length)
      return [
        {
          name: localize('accepted'),
          value:
            (assignmentDetails?.number_accepted /
              assignmentDetails?.assignments?.length) *
            100,
        },
        {
          name: localize('pending'),
          value:
            (assignmentDetails?.number_pending /
              assignmentDetails?.assignments?.length) *
            100,
        },
        {
          name: localize('refused'),
          value:
            (assignmentDetails?.number_refused /
              assignmentDetails?.assignments?.length) *
            100,
        },
        {
          name: localize('expired'),
          value:
            (assignmentDetails?.number_expired /
              assignmentDetails?.assignments?.length) *
            100,
        },
        {
          name: localize('cancelled'),
          value:
            (assignmentDetails?.number_cancelled /
              assignmentDetails?.assignments?.length) *
            100,
        },
      ].filter((element) => element.value !== 0);
    return [];
  }, [
    assignmentDetails?.assignments?.length,
    assignmentDetails?.number_accepted,
    assignmentDetails?.number_cancelled,
    assignmentDetails?.number_expired,
    assignmentDetails?.number_pending,
    assignmentDetails?.number_refused,
  ]);

  const elements: ProgressBarElement[] = details.map((element, index) => {
    return {
      className: classNames(
        'h-[25px] flex items-center justify-center border-y border-[#E7E6E8]',
        index === 0 && 'rounded-l-full border-l',
        index === details.length - 1 && 'rounded-r-full border-r',
        element.name === localize('accepted') && 'bg-[#DAF7F5]',
        element.name === localize('pending') && 'bg-[#FEF7D3]',
        element.name === localize('refused') && 'bg-[#FEE7CC]',
        element.name === localize('expired') && 'bg-[#F7DCDA]',
        element.name === localize('cancelled') && 'bg-[#E7E6E8]',
      ),
      id: index,
      percentage: element.value,
      status: element.name,
    } as ProgressBarElement;
  });

  const containerClassName = classNames(
    'w-80 bg-transparent bg-white flex items-center justify-ceneter rounded-full h-6',
    className,
  );

  if (!assignmentDetails || !elements) return <></>;
  return (
    <div className={containerClassName}>
      {!elements.length && (
        <div className="w-[320px] h-[25px] flex items-center justify-center border-y bg-warning-200 border-[#E7E6E8] rounded-full">
          <Typography
            as="h5"
            variant="body-medium-bold"
            className="text-gray-500 overflow:hidden"
          >
            {localize('noAssignmentFound')}
          </Typography>
        </div>
      )}
      {!!elements.length &&
        elements.map((element) => (
          <Tooltip
            content={`${element.percentage.toFixed(0)}% ${element.status}`}
            position="top"
            key={element.id}
          >
            <div
              key={element.id}
              className={element.className}
              style={{
                width: `${((element.percentage * 319) / 100).toFixed(2)}px`,
              }}
            >
              {(element.percentage * 319) / 100 > 20 && (
                <Typography
                  as="h5"
                  variant="body-medium-bold"
                  className="text-gray-500 overflow:hidden"
                >
                  {Number.isNaN(element.percentage)
                    ? ''
                    : `${element.percentage?.toFixed(0)}%`}
                </Typography>
              )}
            </div>
          </Tooltip>
        ))}
    </div>
  );
};

export default ProgressBar;
